import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  Eye,
  IndianRupee,
  History,
  Trash2,
  CheckCircle,
  Building2,
  Users,
  Zap,
  Pencil,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const OpeningLiabilities = ({ user }) => {
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [utilityTypes, setUtilityTypes] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [liabilitySummary, setLiabilitySummary] = useState({ total_amount: 0, total_balance: 0, total_paid: 0, pending_count: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [filterType, setFilterType] = useState('all'); // 'all', 'vendor', 'employee', 'utility'
  
  // View mode: 'entities' (grouped by entity) or 'individual' (each entry)
  const [viewMode, setViewMode] = useState('entities');
  
  // Entity ledger view (when clicking on a grouped entity)
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityLedgerData, setEntityLedgerData] = useState(null);
  
  // New liability form
  const [showNewLiability, setShowNewLiability] = useState(false);
  const [newLiability, setNewLiability] = useState({ 
    liability_type: 'vendor',
    vendor_id: '', 
    employee_id: '',
    utility_type: '',
    utility_description: '',
    amount: '', 
    remarks: '' 
  });
  
  // Inline Ledger View (like Credit Ledger vendor view)
  const [selectedLiabilityId, setSelectedLiabilityId] = useState(null);
  const [selectedLiabilityDetails, setSelectedLiabilityDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Edit liability state
  const [showEditLiability, setShowEditLiability] = useState(false);
  const [editingLiability, setEditingLiability] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', remarks: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Loading states to prevent double-click
  const [creatingLiability, setCreatingLiability] = useState(false);
  const [deletingLiability, setDeletingLiability] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch ALL employees including terminated for old liabilities
      const response = await axios.get(`${API}/employees?status=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both response formats: {employees: [...]} or direct array
      const employeesList = response.data?.employees || response.data || [];
      console.log('Fetched employees:', employeesList.length);
      setEmployees(employeesList);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employees');
    }
  };

  const fetchUtilityTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/utility-bill-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUtilityTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch utility types:', error);
    }
  };

  const fetchLiabilities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/opening-liabilities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiabilities(response.data.liabilities || []);
      setLiabilitySummary(response.data.summary || { total_amount: 0, total_balance: 0, total_paid: 0, pending_count: 0 });
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch liabilities');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchEmployees();
    fetchUtilityTypes();
    fetchLiabilities();
  }, []);

  const resetNewLiabilityForm = () => {
    setNewLiability({ 
      liability_type: 'vendor',
      vendor_id: '', 
      employee_id: '',
      utility_type: '',
      utility_description: '',
      amount: '', 
      remarks: '' 
    });
  };

  const handleCreateLiability = async (e) => {
    e.preventDefault();
    if (creatingLiability) return; // Prevent double-click
    
    // Validation
    if (newLiability.liability_type === 'vendor' && !newLiability.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (newLiability.liability_type === 'employee' && !newLiability.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (newLiability.liability_type === 'utility' && !newLiability.utility_type) {
      toast.error('Please select a utility type');
      return;
    }
    
    try {
      setCreatingLiability(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        liability_type: newLiability.liability_type,
        amount: parseFloat(newLiability.amount),
        remarks: newLiability.remarks
      };
      
      if (newLiability.liability_type === 'vendor') {
        payload.vendor_id = newLiability.vendor_id;
      } else if (newLiability.liability_type === 'employee') {
        payload.employee_id = newLiability.employee_id;
      } else if (newLiability.liability_type === 'utility') {
        payload.utility_type = newLiability.utility_type;
        payload.utility_description = newLiability.utility_description;
      }
      
      await axios.post(`${API}/opening-liabilities`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Opening liability created');
      setShowNewLiability(false);
      resetNewLiabilityForm();
      fetchLiabilities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create liability'));
    } finally {
      setCreatingLiability(false);
    }
  };

  const viewLiabilityDetails = async (liabilityId) => {
    setLoadingDetails(true);
    setSelectedLiabilityId(liabilityId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/opening-liabilities/${liabilityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLiabilityDetails(response.data);
    } catch (error) {
      toast.error('Failed to load liability details');
      setSelectedLiabilityId(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailView = () => {
    setSelectedLiabilityId(null);
    setSelectedLiabilityDetails(null);
  };

  const handleDeleteLiability = async (liabilityId) => {
    if (deletingLiability) return; // Prevent double-click
    if (!window.confirm('Are you sure you want to delete this opening liability?')) return;
    
    try {
      setDeletingLiability(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/opening-liabilities/${liabilityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Liability deleted');
      fetchLiabilities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete liability'));
    } finally {
      setDeletingLiability(false);
    }
  };

  const handleEditLiability = (liability) => {
    setEditingLiability(liability);
    setEditForm({
      amount: liability.amount.toString(),
      remarks: liability.remarks || ''
    });
    setShowEditLiability(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (savingEdit) return;
    
    try {
      setSavingEdit(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (editForm.amount && parseFloat(editForm.amount) !== editingLiability.amount) {
        params.append('amount', editForm.amount);
      }
      if (editForm.remarks !== (editingLiability.remarks || '')) {
        params.append('remarks', editForm.remarks);
      }
      
      await axios.patch(
        `${API}/opening-liabilities/${editingLiability.id}?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Liability updated successfully');
      setShowEditLiability(false);
      setEditingLiability(null);
      fetchLiabilities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update liability'));
    } finally {
      setSavingEdit(false);
    }
  };

  // Filter liabilities by type
  const filteredLiabilities = filterType === 'all'
    ? liabilities 
    : liabilities.filter(l => (l.liability_type || 'vendor') === filterType);

  // Calculate filtered summary
  const filteredSummary = {
    total_amount: filteredLiabilities.reduce((sum, l) => sum + l.amount, 0),
    total_balance: filteredLiabilities.reduce((sum, l) => sum + l.balance, 0),
    total_paid: filteredLiabilities.reduce((sum, l) => sum + (l.amount - l.balance), 0),
    pending_count: filteredLiabilities.filter(l => l.status === 'active' && l.balance > 0).length
  };

  // Group liabilities by entity name for entity view
  const groupedByEntity = React.useMemo(() => {
    const groups = {};
    filteredLiabilities.forEach(l => {
      // Extract clean entity name (remove "Utility: " prefix for grouping)
      let entityKey = l.entity_name || l.vendor_name || 'Unknown';
      
      if (!groups[entityKey]) {
        groups[entityKey] = {
          entityName: entityKey,
          liabilityType: l.liability_type || 'vendor',
          entries: [],
          totalAmount: 0,
          totalBalance: 0,
          totalPaid: 0,
          entryCount: 0
        };
      }
      groups[entityKey].entries.push(l);
      groups[entityKey].totalAmount += l.amount || 0;
      groups[entityKey].totalBalance += l.balance || 0;
      groups[entityKey].totalPaid += (l.amount - l.balance) || 0;
      groups[entityKey].entryCount++;
    });
    
    // Convert to array and sort by balance descending
    return Object.values(groups).sort((a, b) => b.totalBalance - a.totalBalance);
  }, [filteredLiabilities]);

  // View entity ledger (all entries + payments for an entity)
  const viewEntityLedger = async (entity) => {
    setSelectedEntity(entity.entityName);
    setLoadingDetails(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Fetch details for all liabilities of this entity
      const detailsPromises = entity.entries.map(l =>
        axios.get(`${API}/opening-liabilities/${l.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => res.data).catch(() => null)
      );
      
      const details = await Promise.all(detailsPromises);
      const validDetails = details.filter(d => d !== null);
      
      // Combine all payments from all liabilities
      const allPayments = [];
      validDetails.forEach(d => {
        if (d.payments) {
          d.payments.forEach(p => {
            allPayments.push({
              ...p,
              liabilityRemarks: d.remarks,
              liabilityAmount: d.amount
            });
          });
        }
      });
      
      // Sort payments by date descending
      allPayments.sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at));
      
      setEntityLedgerData({
        entityName: entity.entityName,
        liabilityType: entity.liabilityType,
        entries: entity.entries,
        details: validDetails,
        allPayments,
        totalAmount: entity.totalAmount,
        totalBalance: entity.totalBalance,
        totalPaid: entity.totalPaid,
        entryCount: entity.entryCount
      });
    } catch (error) {
      console.error('Failed to load entity details:', error);
      toast.error('Failed to load entity ledger');
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeEntityLedger = () => {
    setSelectedEntity(null);
    setEntityLedgerData(null);
  };

  return (
    <div className="space-y-6" data-testid="opening-liabilities-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Opening Year Liabilities
          </h1>
          <p className="text-gray-500 text-sm mt-1">Carry-forward balances from previous financial years</p>
        </div>
        {isAdmin && (
          <Dialog open={showNewLiability} onOpenChange={(open) => {
            setShowNewLiability(open);
            if (!open) resetNewLiabilityForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-liability-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Liability
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add Opening Liability</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateLiability} className="space-y-4">
                {/* Liability Type Selection */}
                <div>
                  <Label>Liability Type *</Label>
                  <Select 
                    value={newLiability.liability_type} 
                    onValueChange={(v) => setNewLiability({
                      ...newLiability, 
                      liability_type: v,
                      vendor_id: '',
                      employee_id: '',
                      utility_type: '',
                      utility_description: ''
                    })}
                  >
                    <SelectTrigger data-testid="liability-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendor">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-blue-500" />
                          <span>Vendor</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="employee">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-green-500" />
                          <span>Employee (Salary)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="utility">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          <span>Utility Bill</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Vendor Selection */}
                {newLiability.liability_type === 'vendor' && (
                  <div>
                    <Label>Vendor *</Label>
                    <Select value={newLiability.vendor_id} onValueChange={(v) => setNewLiability({...newLiability, vendor_id: v})}>
                      <SelectTrigger data-testid="vendor-select">
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Employee Selection */}
                {newLiability.liability_type === 'employee' && (
                  <div>
                    <Label>Employee *</Label>
                    {employees.length > 0 ? (
                      <Select value={newLiability.employee_id} onValueChange={(v) => setNewLiability({...newLiability, employee_id: v})}>
                        <SelectTrigger data-testid="employee-select">
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(e => (
                            <SelectItem key={e.id} value={e.id}>
                              <span className={e.status === 'terminated' ? 'text-gray-500' : ''}>
                                {e.name}
                                {e.status === 'terminated' && <span className="text-xs ml-2 text-red-500">(Terminated)</span>}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="border rounded-md p-2 text-sm text-amber-600 bg-amber-50">
                        No employees found. Add employees in Salary Master first.
                      </div>
                    )}
                  </div>
                )}

                {/* Utility Type Selection */}
                {newLiability.liability_type === 'utility' && (
                  <>
                    <div>
                      <Label>Utility Type *</Label>
                      {utilityTypes.length > 0 ? (
                        <Select value={newLiability.utility_type} onValueChange={(v) => setNewLiability({...newLiability, utility_type: v})}>
                          <SelectTrigger data-testid="utility-type-select">
                            <SelectValue placeholder="Select utility type" />
                          </SelectTrigger>
                          <SelectContent>
                            {utilityTypes.map(u => (
                              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="border rounded-md p-2 text-sm text-amber-600 bg-amber-50">
                          No utility types configured. Add utility bills in Settings first.
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input 
                        value={newLiability.utility_description} 
                        onChange={(e) => setNewLiability({...newLiability, utility_description: e.target.value})}
                        placeholder="e.g., Dec 2024 Bill"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label>Amount *</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={newLiability.amount} 
                    onChange={(e) => setNewLiability({...newLiability, amount: e.target.value})}
                    required
                    data-testid="liability-amount-input"
                  />
                </div>
                <div>
                  <Label>Remarks</Label>
                  <Textarea 
                    value={newLiability.remarks} 
                    onChange={(e) => setNewLiability({...newLiability, remarks: e.target.value})}
                    placeholder="e.g., Balance from FY 2024-25"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={creatingLiability} data-testid="submit-liability-btn">
                  {creatingLiability ? 'Creating...' : 'Add Liability'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )} 
      </div>

      {/* Type Filter Buttons & View Mode Toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
            className="gap-2"
            data-testid="filter-all"
          >
            All Types
            <Badge variant="secondary" className="ml-1">{liabilities.length}</Badge>
          </Button>
          <Button
            variant={filterType === 'vendor' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('vendor')}
            className={`gap-2 ${filterType === 'vendor' ? '' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
            data-testid="filter-vendor"
          >
            <Building2 className="w-4 h-4" />
            Vendors
            <Badge variant="secondary" className="ml-1">
              {liabilitySummary.by_type?.vendor?.count || liabilities.filter(l => (l.liability_type || 'vendor') === 'vendor').length}
            </Badge>
          </Button>
          <Button
            variant={filterType === 'employee' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('employee')}
            className={`gap-2 ${filterType === 'employee' ? '' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
            data-testid="filter-employee"
          >
            <Users className="w-4 h-4" />
            Salaries
            <Badge variant="secondary" className="ml-1">
              {liabilitySummary.by_type?.employee?.count || liabilities.filter(l => l.liability_type === 'employee').length}
            </Badge>
          </Button>
          <Button
            variant={filterType === 'utility' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('utility')}
            className={`gap-2 ${filterType === 'utility' ? '' : 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'}`}
            data-testid="filter-utility"
          >
            <Zap className="w-4 h-4" />
            Utilities
            <Badge variant="secondary" className="ml-1">
              {liabilitySummary.by_type?.utility?.count || liabilities.filter(l => l.liability_type === 'utility').length}
            </Badge>
        </Button>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <Button
            variant={viewMode === 'entities' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => { setViewMode('entities'); closeEntityLedger(); }}
            className="gap-1"
          >
            <Building2 className="w-4 h-4" />
            By Entity
          </Button>
          <Button
            variant={viewMode === 'individual' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => { setViewMode('individual'); closeEntityLedger(); }}
            className="gap-1"
          >
            <History className="w-4 h-4" />
            Individual
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <History className="w-4 h-4 text-purple-600" />
              <span className="text-sm text-purple-600 font-medium">
                {filterType === 'all' ? 'Total Liabilities' : `${filterType.charAt(0).toUpperCase() + filterType.slice(1)} Liabilities`}
              </span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{formatCurrency(filteredSummary.total_amount)}</p>
            <p className="text-xs text-purple-500">{filteredLiabilities.length} entries</p>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-600 font-medium">Pending Balance</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{formatCurrency(filteredSummary.total_balance)}</p>
            <p className="text-xs text-red-500">{filteredSummary.pending_count} pending</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600 font-medium">Total Paid</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(filteredSummary.total_paid)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-600 font-medium">Payment Progress</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">
              {filteredSummary.total_amount > 0 
                ? (filteredSummary.total_balance <= 0 
                    ? 100 
                    : Math.floor((filteredSummary.total_paid / filteredSummary.total_amount) * 100))
                : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Liabilities Table OR Detail View */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              {selectedLiabilityId ? (
                <>
                  <Button variant="ghost" size="sm" onClick={closeDetailView} className="mr-2">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                  <History className="w-5 h-5 text-purple-600" />
                  {selectedLiabilityDetails?.entity_name || 'Liability'} - Ledger
                </>
              ) : selectedEntity ? (
                <>
                  <Button variant="ghost" size="sm" onClick={closeEntityLedger} className="mr-2">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                  <Building2 className="w-5 h-5 text-purple-600" />
                  {selectedEntity} - Entity Ledger
                </>
              ) : (
                filterType === 'all' ? 'All Opening Liabilities' : `${filterType.charAt(0).toUpperCase() + filterType.slice(1)} Liabilities`
              )}
            </CardTitle>
            {selectedLiabilityId && selectedLiabilityDetails && (
              <Badge className={`text-sm px-3 py-1 ${selectedLiabilityDetails.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {selectedLiabilityDetails.status?.toUpperCase()}
              </Badge>
            )}
            {selectedEntity && entityLedgerData && (
              <Badge className={`text-sm px-3 py-1 ${entityLedgerData.totalBalance <= 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {entityLedgerData.totalBalance <= 0 ? 'PAID' : 'ACTIVE'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {selectedLiabilityId ? (
            /* Inline Ledger Detail View */
            loadingDetails ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                <p className="mt-2 text-gray-500">Loading ledger...</p>
              </div>
            ) : selectedLiabilityDetails ? (
              <div className="space-y-6">
                {/* Summary Cards - 4 columns like Credit Ledger */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-blue-600 font-medium">Liability Type</p>
                      <p className="text-lg font-bold text-blue-700 capitalize">{selectedLiabilityDetails.liability_type || 'Vendor'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-purple-600 font-medium">Original Amount</p>
                      <p className="text-lg font-bold text-purple-700">{formatCurrency(selectedLiabilityDetails.amount)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-green-600 font-medium">Total Paid</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(selectedLiabilityDetails.amount - selectedLiabilityDetails.balance)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-red-600 font-medium">Pending</p>
                      <p className="text-lg font-bold text-red-700">{formatCurrency(selectedLiabilityDetails.balance)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Two Column Layout - Opening Entry & Payments */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Left: Opening Liability */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" /> Opening Liability
                    </h3>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader className="bg-gray-50">
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow className="bg-purple-50/30">
                            <TableCell className="font-medium">
                              {new Date(selectedLiabilityDetails.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' })}
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{selectedLiabilityDetails.entity_name || selectedLiabilityDetails.vendor_name}</span>
                              {selectedLiabilityDetails.remarks && (
                                <span className="text-xs text-gray-500 block">{selectedLiabilityDetails.remarks}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(selectedLiabilityDetails.amount)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-red-600">
                              {formatCurrency(selectedLiabilityDetails.balance)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Right: Payments Made */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <IndianRupee className="w-4 h-4" /> Payments Made
                    </h3>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader className="bg-gray-50">
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Paid By</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Method</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedLiabilityDetails.payments && selectedLiabilityDetails.payments.length > 0 ? (
                            [...selectedLiabilityDetails.payments]
                              .sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at))
                              .map((p) => (
                                <TableRow key={p.id} className="hover:bg-gray-50">
                                  <TableCell>
                                    {new Date(p.paid_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' })}
                                  </TableCell>
                                  <TableCell>{p.paid_by_name}</TableCell>
                                  <TableCell className="text-right text-green-600 font-medium">
                                    {formatCurrency(p.amount)}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className={p.payment_method === 'cash' ? 'bg-yellow-50' : 'bg-blue-50'}>
                                      {p.payment_method}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                                No payments recorded yet
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>

                {/* Footer Info */}
                <div className="text-xs text-gray-500 text-right">
                  Created by {selectedLiabilityDetails.created_by_name} • Last updated: {new Date(selectedLiabilityDetails.updated_at || selectedLiabilityDetails.created_at).toLocaleString('en-IN')}
                </div>
              </div>
            ) : null
          ) : selectedEntity && entityLedgerData ? (
            /* Entity Ledger View - Shows all entries and payments for a single entity */
            loadingDetails ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                <p className="mt-2 text-gray-500">Loading entity ledger...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-blue-600 font-medium">Liability Type</p>
                      <p className="text-lg font-bold text-blue-700 capitalize">{entityLedgerData.liabilityType || 'Vendor'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-purple-600 font-medium">Total Amount</p>
                      <p className="text-lg font-bold text-purple-700">{formatCurrency(entityLedgerData.totalAmount)}</p>
                      <p className="text-xs text-purple-500">{entityLedgerData.entryCount} entries</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-green-600 font-medium">Total Paid</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(entityLedgerData.totalPaid)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-red-600 font-medium">Pending Balance</p>
                      <p className="text-lg font-bold text-red-700">{formatCurrency(entityLedgerData.totalBalance)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Two Column Layout - Liability Entries & Payments */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Left: All Liability Entries */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" /> Liability Entries ({entityLedgerData.entryCount})
                    </h3>
                    <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader className="bg-gray-50 sticky top-0">
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Remarks</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entityLedgerData.entries.map((entry, idx) => (
                            <TableRow key={entry.id} className={idx % 2 === 0 ? 'bg-purple-50/30' : ''}>
                              <TableCell className="text-sm">
                                {new Date(entry.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' })}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {entry.remarks || '-'}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(entry.amount)}
                              </TableCell>
                              <TableCell className="text-right font-semibold text-red-600">
                                {formatCurrency(entry.balance)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Right: All Payments Made */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <IndianRupee className="w-4 h-4" /> Payments Made ({entityLedgerData.allPayments.length})
                    </h3>
                    <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader className="bg-gray-50 sticky top-0">
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Paid By</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Method</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entityLedgerData.allPayments.length > 0 ? (
                            entityLedgerData.allPayments.map((p, idx) => (
                              <TableRow key={p.id || idx} className="hover:bg-gray-50">
                                <TableCell className="text-sm">
                                  {new Date(p.paid_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' })}
                                </TableCell>
                                <TableCell className="text-sm">{p.paid_by_name}</TableCell>
                                <TableCell className="text-right text-green-600 font-medium">
                                  {formatCurrency(p.amount)}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={p.payment_method === 'cash' ? 'bg-yellow-50' : 'bg-blue-50'}>
                                    {p.payment_method}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                                No payments recorded yet
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : (
            /* List View - Entity Grouped or Individual */
            loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : viewMode === 'entities' ? (
              /* Entity Grouped View */
              groupedByEntity.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {filterType === 'all' 
                    ? `No opening liabilities found. ${isAdmin ? 'Click "Add Liability" to add carry-forward balances from previous year.' : ''}`
                    : `No ${filterType} liabilities found.`}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Entity Name</TableHead>
                      <TableHead className="text-center">Entries</TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                      <TableHead className="text-right">Pending Balance</TableHead>
                      <TableHead className="text-right">Total Paid</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedByEntity.map((entity) => (
                      <TableRow 
                        key={entity.entityName} 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => viewEntityLedger(entity)}
                        data-testid={`entity-row-${entity.entityName}`}
                      >
                        <TableCell>
                          <Badge variant="outline" className={
                            entity.liabilityType === 'vendor' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                            entity.liabilityType === 'employee' ? 'border-green-300 text-green-700 bg-green-50' :
                            'border-yellow-300 text-yellow-700 bg-yellow-50'
                          }>
                            {entity.liabilityType === 'vendor' && <Building2 className="w-3 h-3 mr-1" />}
                            {entity.liabilityType === 'employee' && <Users className="w-3 h-3 mr-1" />}
                            {entity.liabilityType === 'utility' && <Zap className="w-3 h-3 mr-1" />}
                            {entity.liabilityType.charAt(0).toUpperCase() + entity.liabilityType.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {entity.entityName}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{entity.entryCount}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(entity.totalAmount)}</TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatCurrency(entity.totalBalance)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(entity.totalPaid)}
                        </TableCell>
                        <TableCell>
                          <Badge className={entity.totalBalance <= 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {entity.totalBalance <= 0 ? 'PAID' : 'ACTIVE'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            ) : filteredLiabilities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {filterType === 'all' 
                  ? `No opening liabilities found. ${isAdmin ? 'Click "Add Liability" to add carry-forward balances from previous year.' : ''}`
                  : `No ${filterType} liabilities found.`}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead className="text-right">Original Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLiabilities.map((liability) => (
                    <TableRow 
                      key={liability.id} 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => viewLiabilityDetails(liability.id)}
                    >
                      <TableCell>
                        <Badge variant="outline" className={
                          liability.liability_type === 'vendor' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                          liability.liability_type === 'employee' ? 'border-green-300 text-green-700 bg-green-50' :
                          'border-yellow-300 text-yellow-700 bg-yellow-50'
                        }>
                          {liability.liability_type === 'vendor' && <Building2 className="w-3 h-3 mr-1" />}
                          {liability.liability_type === 'employee' && <Users className="w-3 h-3 mr-1" />}
                          {liability.liability_type === 'utility' && <Zap className="w-3 h-3 mr-1" />}
                          {(liability.liability_type || 'vendor').charAt(0).toUpperCase() + (liability.liability_type || 'vendor').slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {liability.entity_name || liability.vendor_name || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(liability.amount)}</TableCell>
                      <TableCell className="text-right font-semibold text-red-600">
                        {formatCurrency(liability.balance)}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      {formatCurrency(liability.amount - liability.balance)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 max-w-xs truncate">{liability.remarks || '-'}</TableCell>
                    <TableCell>
                      <Badge className={liability.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                        {liability.status === 'paid' ? 'PAID' : 'ACTIVE'}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewLiabilityDetails(liability.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => handleEditLiability(liability)}
                            title="Edit liability"
                            disabled={savingEdit || deletingLiability}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {isAdmin && liability.amount === liability.balance && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteLiability(liability.id)}
                            title="Delete liability"
                            disabled={deletingLiability || savingEdit}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            )
          )}
        </CardContent>
      </Card>

      {/* Edit Liability Dialog */}
      <Dialog open={showEditLiability} onOpenChange={setShowEditLiability}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Liability</DialogTitle>
          </DialogHeader>
          {editingLiability && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <Label>Entity</Label>
                <Input 
                  value={editingLiability.entity_name || editingLiability.vendor_name || 'Unknown'}
                  disabled
                  className="bg-gray-50"
                />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={editForm.amount} 
                  onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                  required
                />
                {editingLiability.amount !== editingLiability.balance && (
                  <p className="text-xs text-blue-600 mt-1">
                    Paid: ₹{(editingLiability.amount - editingLiability.balance).toLocaleString('en-IN')} • 
                    New balance will be recalculated
                  </p>
                )}
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea 
                  value={editForm.remarks} 
                  onChange={(e) => setEditForm({...editForm, remarks: e.target.value})}
                  placeholder="Optional remarks"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditLiability(false)}
                  className="flex-1"
                  disabled={savingEdit}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={savingEdit}
                  className="flex-1"
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpeningLiabilities;
